import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import type {
  DismantlingMethodCreateInput,
  DismantlingMethodMaster,
  DismantlingMethodUpdateInput,
} from './types';

/**
 * Endpoints (see `backend/src/modules/dismantling_methods/routes.rs`):
 *   GET    /api/dismantling-methods            ?company_id=&active=
 *   POST   /api/dismantling-methods
 *   PUT    /api/dismantling-methods/:id
 *   DELETE /api/dismantling-methods/:id        (deactivate)
 *   POST   /api/dismantling-methods/:id/activate
 *   DELETE /api/dismantling-methods/:id/purge
 *
 * The company is taken from the session unless one is named, so an operator
 * never has to know their own company id.
 */

export interface DismantlingMethodListParams {
  /** Read the list that applies to a batch — the processor doing the work.
   *  This is the same list the server validates a session against, so a
   *  registration screen must always ask this way rather than by company. */
  monthly_batch_id?: string;
  company_id?: string;
  active?: boolean;
}

export const listDismantlingMethods = async (
  params: DismantlingMethodListParams = {},
): Promise<DismantlingMethodMaster[]> => {
  const res = await apiClient.get<ApiSuccess<DismantlingMethodMaster[]>>('/dismantling-methods', {
    params,
  });
  return res.data.data ?? [];
};

export const createDismantlingMethod = async (
  body: DismantlingMethodCreateInput,
): Promise<DismantlingMethodMaster> => {
  const res = await apiClient.post<ApiSuccess<DismantlingMethodMaster>>(
    '/dismantling-methods',
    body,
  );
  return res.data.data;
};

export const updateDismantlingMethod = async (
  id: string,
  body: DismantlingMethodUpdateInput,
): Promise<DismantlingMethodMaster> => {
  const res = await apiClient.put<ApiSuccess<DismantlingMethodMaster>>(
    `/dismantling-methods/${id}`,
    body,
  );
  return res.data.data;
};

export const deactivateDismantlingMethod = async (id: string): Promise<DismantlingMethodMaster> => {
  const res = await apiClient.delete<ApiSuccess<DismantlingMethodMaster>>(
    `/dismantling-methods/${id}`,
  );
  return res.data.data;
};

export const activateDismantlingMethod = async (id: string): Promise<DismantlingMethodMaster> => {
  const res = await apiClient.post<ApiSuccess<DismantlingMethodMaster>>(
    `/dismantling-methods/${id}/activate`,
  );
  return res.data.data;
};

export const purgeDismantlingMethod = async (id: string): Promise<void> => {
  await apiClient.delete(`/dismantling-methods/${id}/purge`);
};
